import { ref, onUnmounted } from 'vue';

// // Matches the 1s CSS transition duration on both TvP08ButtonComp's progress-fill and
// // TvP08ProgressCircleComp's ring-fill — see FILL_DRAIN_GRACE_MS below.
const FILL_DRAIN_GRACE_MS = 1000;

/**
 * Per-stage countdown composable for PickSellerAndOfferings-style flows: each stage shows a
 * seconds-remaining countdown (as a Progress Circle or a Button's progress-fill) and auto-fires
 * `onExpire` once it reaches 0. The countdown only (re)starts on `start(seconds)` — call it when
 * entering a stage. It does NOT reset on user input; navigating within a stage doesn't extend it.
 */
export function useAutoDismissCountdown(onExpire: () => void) {
  const secondsRemaining = ref(0);
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let expireTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (expireTimeoutId) {
      clearTimeout(expireTimeoutId);
      expireTimeoutId = null;
    }
  };

  const start = (seconds: number) => {
    clear();
    secondsRemaining.value = seconds;
    intervalId = setInterval(() => {
      secondsRemaining.value -= 1;
      if (secondsRemaining.value <= 0) {
        clear();
        // // secondsRemaining hitting 0 only *starts* the fill's CSS transition toward a clean
        // // empty state — it doesn't mean the fill has visually finished draining yet. Firing
        // // onExpire (which triggers the overlay's own, faster 0.5s fade-out) immediately would
        // // cut that transition off mid-drain, so the ring/bar never visibly reaches empty.
        // // Give the fill's own transition time to actually finish first.
        expireTimeoutId = setTimeout(onExpire, FILL_DRAIN_GRACE_MS);
      }
    }, 1000);
  };

  const reset = (seconds: number) => {
    start(seconds);
  };

  const stop = () => {
    clear();
  };

  onUnmounted(() => {
    clear();
  });

  return {
    secondsRemaining,
    start,
    reset,
    stop,
  };
}
